import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { ProjectContractTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { deleteBimOrderChangeGql } from './deleteBimOrderChangeQueries';

export const useDeleteBimOrderChange = ({
    workOrderChangeId,
    workOrderId,
}: {
    workOrderChangeId: number;
    workOrderId: number;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: projectId, methodology, orderChangeId } = useProjectParams();
    const { pathname } = useLocation();
    const { projectData, refreshProject } = useProjectData(projectId, false);

    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const deleteBimOrderChangeVariables = useMemo(() => ({ workOrderChangeId }), [workOrderChangeId]);

    const isFrameProject = projectData?.contractType === ProjectContractTypeEnum.Frame;
    const orderChangeDeleteBtnTitle = `Удалить Изм. к ${isFrameProject ? 'НЗ' : 'Расчету по Договору'}`;
    const orderChangeDeleteModalTitle = `Удаление Изм. к ${isFrameProject ? 'НЗ' : 'Расчету по Договору'}`;
    const orderChangeDeleteConfirmTitle = `Вы уверены, что хотите удалить Изменение к ${
        isFrameProject ? 'НЗ' : 'Расчету по Договору'
    }?`;
    const orderChangeDeleteTooltipText = `${orderChangeDeleteModalTitle} возможно только в статусе "На формировании"`;

    const requestId = useMemo(() => `deleteBimOrderChange${workOrderChangeId}`, [workOrderChangeId]);

    const [resetData] = useResetStoreData({
        requestId,
    });

    const [deleteBimOrderChangeData, deleteBimOrderChangeStatus, deleteBimOrderChangeErr, deleteBimOrderChangeRequest] =
        useRequest({
            requestId,
            query: deleteBimOrderChangeGql,
            variables: deleteBimOrderChangeVariables,
            withEffect: false,
        });

    useEffect(() => {
        if (
            deleteBimOrderChangeStatus === RequestStatus.SUCCESS &&
            deleteBimOrderChangeData?.bimDeleteWorkOrderChange?.result
        ) {
            if (deleteBimOrderChangeData?.bimDeleteWorkOrderChange?.id === Number(orderChangeId)) {
                const newPathName = `/projects/${methodology}/${projectId}/order/${workOrderId}`;

                navigate(newPathName);
            }

            resetData(undefined);
            refreshProject();
        }
    }, [
        projectId,
        pathname,
        navigate,
        refreshProject,
        methodology,
        deleteBimOrderChangeStatus,
        deleteBimOrderChangeData?.bimDeleteWorkOrderChange,
        orderChangeId,
        workOrderId,
        resetData,
    ]);

    const handleDeleteBimOrderChange = useCallback(() => {
        resetData(undefined);
        dispatch(deleteBimOrderChangeRequest(deleteBimOrderChangeVariables));
        setIsSubmitted(true);
    }, [deleteBimOrderChangeRequest, deleteBimOrderChangeVariables, dispatch, resetData]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handleDeleteBimOrderChange,
        });

    const handleCloseErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(deleteBimOrderChangeData?.bimDeleteWorkOrderChange?.validation);
    }, [deleteBimOrderChangeData?.bimDeleteWorkOrderChange?.validation]);

    const DeleteOrderChangeErrorHandler = useMemo(
        () => (
            <ErrorHandlingModal
                titleText={orderChangeDeleteModalTitle}
                isSubmitted={isSubmitted}
                statusRequest={deleteBimOrderChangeStatus}
                dataRequest={deleteBimOrderChangeData?.bimDeleteWorkOrderChange}
                errorsRequest={deleteBimOrderChangeErr}
                handleCloseModalError={handleCloseErrorModal}
                handleCreateReport={handleCreateReport}
            />
        ),
        [
            deleteBimOrderChangeData?.bimDeleteWorkOrderChange,
            deleteBimOrderChangeErr,
            deleteBimOrderChangeStatus,
            handleCloseErrorModal,
            handleCreateReport,
            isSubmitted,
            orderChangeDeleteModalTitle,
        ],
    );

    const DeleteOrderChangeConfirmDialog = useMemo(
        () => (
            <ConfirmationDialog
                textHeader={orderChangeDeleteModalTitle}
                textConfirmation={orderChangeDeleteConfirmTitle}
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        ),
        [
            handleActionQuery,
            handleCloseConfirmationDialog,
            isOpenConfirmationDialog,
            orderChangeDeleteModalTitle,
            orderChangeDeleteConfirmTitle,
        ],
    );

    const deleteBimOrderChangeHandler = useCallback(() => {
        handleOpenConfirmationDialog();
    }, [handleOpenConfirmationDialog]);

    return {
        orderChangeDeleteBtnTitle,
        deleteBimOrderChangeHandler,
        DeleteOrderChangeErrorHandler,
        DeleteOrderChangeConfirmDialog,
        orderChangeDeleteTooltipText,
    };
};
